import React, { useState, useEffect } from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { format, parseISO } from 'date-fns';
import { TimeEntry, timeEntriesAPI } from '../../services/api';
import ComplianceCoach from './ComplianceCoach';
import {
  SearchIcon,
  CheckIcon,
  XIcon,
  CheckCircleIcon,
  ClockIcon,
  FolderIcon,
  BeakerIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FilterIcon,
} from '../icons';

// Entries scoring below this are flagged by the pre-submission compliance gate.
const COMPLIANCE_THRESHOLD = 60;

interface TimeEntryWithDetails {
  id?: number;
  entry_date?: string;
  start_time?: string;
  end_time?: string;
  hours_worked?: number;
  description: string;
  technical_challenge?: string;
  advancement?: string;
  is_sred_eligible?: boolean;
  is_cdae_eligible?: boolean;
  approval_status?: string;
  ai_confidence_score?: number;
  user: {
    id: number;
    full_name: string;
    email: string;
  };
  project: {
    id: number;
    name: string;
  };
  sred_activity_category: {
    id: number;
    name: string;
    code: string;
  };
}

// Premium Icons — bodies live in the shared icon library; classNames and
// strokeWidths preserved so rendering is identical.
const Icons = {
  search: <SearchIcon className="w-5 h-5" strokeWidth={1.5} />,
  check: <CheckIcon className="w-4 h-4" strokeWidth={2} />,
  x: <XIcon className="w-4 h-4" strokeWidth={2} />,
  checkCircle: <CheckCircleIcon className="w-12 h-12" strokeWidth={1.5} />,
  clock: <ClockIcon className="w-5 h-5" strokeWidth={1.5} />,
  folder: <FolderIcon className="w-4 h-4" strokeWidth={1.5} />,
  beaker: <BeakerIcon className="w-4 h-4" strokeWidth={1.5} />,
  chevronLeft: <ChevronLeftIcon className="w-5 h-5" strokeWidth={1.5} />,
  chevronDown: <ChevronDownIcon className="w-5 h-5" strokeWidth={1.5} />,
  chevronRight: <ChevronRightIcon className="w-5 h-5" strokeWidth={1.5} />,
  filter: <FilterIcon className="w-4 h-4" strokeWidth={2} />,
};

const TimeApprovalList: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [pendingEntries, setPendingEntries] = useState<TimeEntryWithDetails[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState<Set<number>>(
    new Set(),
  );
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    fetchPendingEntries();
  }, []);

  const fetchPendingEntries = async () => {
    try {
      const response = await timeEntriesAPI.getAll({ status: 'pending' });
      setPendingEntries(response.data.time_entries || []);
    } catch (error) {
      console.error('Failed to fetch pending entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedEntries(new Set(filteredEntries.map((entry) => entry.id!)));
    } else {
      setSelectedEntries(new Set());
    }
  };

  const handleSelectEntry = (entryId: number, checked: boolean) => {
    const newSelected = new Set(selectedEntries);
    if (checked) {
      newSelected.add(entryId);
    } else {
      newSelected.delete(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleSelectUserEntries = (
    userEntries: TimeEntryWithDetails[],
    checked: boolean,
  ) => {
    const newSelected = new Set(selectedEntries);
    userEntries.forEach((entry) => {
      if (entry.id) {
        if (checked) {
          newSelected.add(entry.id);
        } else {
          newSelected.delete(entry.id);
        }
      }
    });
    setSelectedEntries(newSelected);
  };

  const getUserSelectionState = (userEntries: TimeEntryWithDetails[]) => {
    const selectedCount = userEntries.filter(
      (e) => e.id && selectedEntries.has(e.id),
    ).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === userEntries.length) return 'all';
    return 'some';
  };

  const handleBulkApprove = async () => {
    if (selectedEntries.size === 0) {
      showToast(t('approval.selectEntries'), 'warning');
      return;
    }

    // Pre-approval compliance gate: warn if any selected entry scores below
    // the threshold, so weak SR&ED/CDAE descriptions can be fixed first.
    const lowScoring = pendingEntries.filter(
      (e) =>
        e.id &&
        selectedEntries.has(e.id) &&
        typeof e.ai_confidence_score === 'number' &&
        e.ai_confidence_score < COMPLIANCE_THRESHOLD,
    );

    if (lowScoring.length > 0) {
      const proceed = await confirm({
        title: t('approval.complianceGateTitle', {
          defaultValue: 'Low compliance scores',
        }),
        message: t('approval.complianceGateMessage', {
          count: lowScoring.length,
          threshold: COMPLIANCE_THRESHOLD,
          defaultValue: `${lowScoring.length} of the selected entries score below ${COMPLIANCE_THRESHOLD}% compliance. Approving them as-is may weaken your SR&ED/CDAE claim. Approve anyway?`,
        }),
        confirmLabel: t('approval.approveAnyway', {
          defaultValue: 'Approve anyway',
        }),
        cancelLabel: t('common.cancel', { defaultValue: 'Cancel' }),
        variant: 'warning',
      });
      if (!proceed) return;
    }

    try {
      await timeEntriesAPI.bulkApprove(Array.from(selectedEntries));
      showToast(
        t('approval.bulkApproveSuccess', { count: selectedEntries.size }),
        'success',
      );
      fetchPendingEntries();
      setSelectedEntries(new Set());
    } catch (error) {
      console.error('Failed to bulk approve:', error);
      showToast(t('approval.approveFailed'), 'error');
    }
  };

  const handleIndividualApprove = async (entryId: number) => {
    try {
      await timeEntriesAPI.approve(entryId);
      showToast(t('approval.approveSuccess'), 'success');
      fetchPendingEntries();
    } catch (error) {
      console.error('Failed to approve entry:', error);
      showToast(t('approval.approveFailed'), 'error');
    }
  };

  // Get unique projects for filter dropdown
  const uniqueProjects = Array.from(
    new Map(pendingEntries.map((e) => [e.project?.id, e.project])).values(),
  ).filter(Boolean);

  // Filter entries
  const filteredEntries = pendingEntries.filter((entry) => {
    const matchesUser =
      !userFilter ||
      entry.user?.full_name?.toLowerCase().includes(userFilter.toLowerCase());
    const matchesProject =
      !projectFilter || entry.project?.id?.toString() === projectFilter;
    return matchesUser && matchesProject;
  });

  const groupedEntries = filteredEntries.reduce(
    (groups: { [key: string]: TimeEntryWithDetails[] }, entry) => {
      const userKey = `${entry.user?.full_name || t('common.unknown')}_${entry.user?.id || 0}`;
      if (!groups[userKey]) {
        groups[userKey] = [];
      }
      groups[userKey].push(entry);
      return groups;
    },
    {},
  );

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm rounded-2xl'
    : 'bg-white border border-slate-200 shadow-sm rounded-2xl';

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'} min-h-screen`}
    >
      {/* Pre-submission compliance coach */}
      <ComplianceCoach
        threshold={COMPLIANCE_THRESHOLD}
        refreshKey={pendingEntries}
      />

      {/* Sticky Header */}
      <div
        className={`sticky top-0 z-20 ${isDark ? 'bg-slate-900' : 'bg-slate-50'} pb-4 -mx-6 px-6 pt-0`}
      >
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`hidden sm:flex p-2 rounded-xl ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}
              >
                <span
                  className={isDark ? 'text-emerald-400' : 'text-emerald-600'}
                >
                  {Icons.check}
                </span>
              </div>
              <h1
                className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('approval.title')}
              </h1>
            </div>
            <p
              className={`text-sm sm:text-base ${isDark ? 'text-slate-400' : 'text-slate-600'} sm:ml-14`}
            >
              {t('approval.pendingCount', { count: pendingEntries.length })}
            </p>
          </div>
          <button
            onClick={handleBulkApprove}
            disabled={selectedEntries.size === 0}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {Icons.check}
            <span>
              {t('approval.approveSelected', { count: selectedEntries.size })}
            </span>
          </button>
        </div>

        {/* Filters */}
        <div className={`${cardClass} p-4 mb-4`}>
          {/* Search always visible */}
          <div className="relative">
            <span
              className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
            >
              {Icons.search}
            </span>
            <input
              type="text"
              placeholder={t('approval.searchByUser')}
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className={`w-full pl-11 pr-4 py-2.5 rounded-xl transition-colors ${
                isDark
                  ? 'bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 focus:border-violet-500'
                  : 'bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-violet-500'
              }`}
            />
          </div>

          {/* Mobile filter toggle */}
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`md:hidden w-full flex items-center justify-between mt-3 py-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              {Icons.filter}
              {t('common.filters')}
              {projectFilter && (
                <span className="px-1.5 py-0.5 text-xs rounded-full bg-violet-500 text-white">
                  1
                </span>
              )}
            </span>
            <span
              className={`transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}
            >
              {Icons.chevronDown}
            </span>
          </button>

          {/* Collapsible filters - visible on desktop, toggleable on mobile */}
          <div
            className={`${filtersOpen ? 'block' : 'hidden'} md:block mt-3 md:mt-4`}
          >
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className={`w-full md:w-auto px-4 py-2.5 rounded-xl transition-colors ${
                isDark
                  ? 'bg-slate-900/50 border border-slate-700 text-white focus:border-violet-500'
                  : 'bg-white border border-slate-200 text-slate-900 focus:border-violet-500'
              }`}
            >
              <option value="">{t('approval.allProjects')}</option>
              {uniqueProjects.map((project) => (
                <option key={project?.id} value={project?.id}>
                  {project?.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bulk Selection - also sticky */}
        {filteredEntries.length > 0 && (
          <div className={`${cardClass} p-3 sm:p-4`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 sm:gap-3">
                <input
                  type="checkbox"
                  checked={
                    selectedEntries.size === filteredEntries.length &&
                    filteredEntries.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-slate-600 rounded bg-slate-900"
                />
                <span
                  className={`text-xs sm:text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  {t('approval.selectAll', { count: filteredEntries.length })}
                </span>
              </div>
              <div
                className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('approval.totalHours', {
                  hours: filteredEntries
                    .reduce(
                      (sum, entry) =>
                        sum +
                        (parseFloat(entry.hours_worked?.toString() || '0') ||
                          0),
                      0,
                    )
                    .toFixed(1),
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {filteredEntries.length === 0 ? (
        <div className={`${cardClass} p-12 text-center`}>
          <div className="text-emerald-500 mb-4 flex justify-center">
            {Icons.checkCircle}
          </div>
          <h2
            className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {pendingEntries.length === 0
              ? t('approval.allCaughtUp')
              : t('approval.noResults')}
          </h2>
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            {pendingEntries.length === 0
              ? t('approval.noPending')
              : t('approval.tryDifferentFilters')}
          </p>
        </div>
      ) : (
        <div className="space-y-3 mt-4">
          {/* Accordion - Grouped Entries by User */}
          {Object.entries(groupedEntries).map(([userKey, userEntries]) => {
            const user = userEntries[0].user;
            const totalHours = userEntries.reduce(
              (sum, entry) =>
                sum + (parseFloat(entry.hours_worked?.toString() || '0') || 0),
              0,
            );
            const isExpanded = expandedUser === userKey;
            const userSelectionState = getUserSelectionState(userEntries);

            return (
              <div key={userKey} className={cardClass}>
                {/* Accordion Header - Clickable */}
                <div
                  className={`w-full px-4 sm:px-6 py-4 ${isExpanded ? `border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'}` : ''} ${isDark ? 'bg-slate-900/50' : 'bg-slate-50'} rounded-t-2xl ${!isExpanded ? 'rounded-b-2xl' : ''} transition-colors`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <input
                        type="checkbox"
                        checked={userSelectionState === 'all'}
                        ref={(el) => {
                          if (el)
                            el.indeterminate = userSelectionState === 'some';
                        }}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectUserEntries(
                            userEntries,
                            e.target.checked,
                          );
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 flex-shrink-0 text-violet-600 focus:ring-violet-500 border-slate-600 rounded bg-slate-900"
                      />
                      <button
                        onClick={() =>
                          setExpandedUser(isExpanded ? null : userKey)
                        }
                        className={`flex items-center gap-2 sm:gap-3 min-w-0 flex-1 ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-100'} rounded-xl px-2 py-1 -ml-2 transition-colors`}
                      >
                        <span
                          className={`flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                        >
                          {Icons.chevronRight}
                        </span>
                        <div className="hidden sm:flex h-10 w-10 flex-shrink-0 rounded-xl items-center justify-center bg-gradient-to-br from-violet-500 to-purple-600">
                          <span className="text-sm font-semibold text-white">
                            {user?.full_name
                              ?.split(' ')
                              .map((n) => n[0])
                              .join('') || 'UN'}
                          </span>
                        </div>
                        <div className="text-left min-w-0 flex-1">
                          <h3
                            className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                          >
                            {user?.full_name || t('common.unknown')}
                          </h3>
                          <p
                            className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                          >
                            {user?.email}
                          </p>
                        </div>
                      </button>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div
                        className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {t('approval.hoursValue', {
                          hours: (totalHours || 0).toFixed(1),
                        })}
                      </div>
                      <div
                        className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                      >
                        {t('approval.entriesCount', {
                          count: userEntries.length,
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Entries - Only shown when expanded */}
                {isExpanded && (
                  <div
                    className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-100'}`}
                  >
                    {userEntries.map((entry) => (
                      <div key={entry.id} className="px-4 sm:px-6 py-4">
                        <div className="grid grid-cols-[10fr] md:flex md:items-start gap-2 sm:gap-4">
                          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                            <input
                              type="checkbox"
                              checked={selectedEntries.has(entry.id!)}
                              onChange={(e) =>
                                handleSelectEntry(entry.id!, e.target.checked)
                              }
                              className="mt-1 h-4 w-4 flex-shrink-0 text-violet-600 focus:ring-violet-500 border-slate-600 rounded bg-slate-900"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center flex-wrap gap-2 sm:gap-3 mb-2">
                                <span
                                  className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                                >
                                  {entry.entry_date
                                    ? format(
                                        parseISO(entry.entry_date),
                                        'M/d/yyyy',
                                      )
                                    : t('timeEntry.noDate')}
                                </span>
                                <span
                                  className={`inline-flex items-center gap-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                >
                                  {Icons.clock}
                                  {(
                                    parseFloat(
                                      entry.hours_worked?.toString() || '0',
                                    ) || 0
                                  ).toFixed(1)}
                                  h
                                </span>
                                {entry.start_time && entry.end_time && (
                                  <span
                                    className={`hidden sm:inline text-sm ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                                  >
                                    {entry.start_time} - {entry.end_time}
                                  </span>
                                )}
                              </div>

                              <p
                                className={`text-sm mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                              >
                                {entry.description}
                              </p>

                              <div className="flex items-center flex-wrap gap-3 text-xs">
                                {entry.project && (
                                  <span
                                    className={`inline-flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                  >
                                    {Icons.folder} {entry.project.name}
                                  </span>
                                )}
                                {entry.sred_activity_category && (
                                  <span
                                    className={`inline-flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                                  >
                                    {Icons.beaker}{' '}
                                    {entry.sred_activity_category.name}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center flex-wrap gap-2 mt-3">
                                {entry.is_sred_eligible && (
                                  <span
                                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${
                                      isDark
                                        ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                                        : 'bg-violet-100 text-violet-700'
                                    }`}
                                  >
                                    {t('compliance.sredEligible')}
                                  </span>
                                )}
                                {entry.is_cdae_eligible && (
                                  <span
                                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${
                                      isDark
                                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                        : 'bg-cyan-100 text-cyan-700'
                                    }`}
                                  >
                                    {t('compliance.cdaeEligible')}
                                  </span>
                                )}
                              </div>

                              {(entry.technical_challenge ||
                                entry.advancement) && (
                                <div
                                  className={`mt-3 p-3 rounded-xl ${isDark ? 'bg-slate-900/50' : 'bg-slate-50'}`}
                                >
                                  {entry.technical_challenge && (
                                    <div className="mb-2">
                                      <span
                                        className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                      >
                                        {t('compliance.technicalChallenge')}:
                                      </span>
                                      <p
                                        className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
                                      >
                                        {entry.technical_challenge}
                                      </p>
                                    </div>
                                  )}
                                  {entry.advancement && (
                                    <div>
                                      <span
                                        className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                                      >
                                        {t('compliance.advancement')}:
                                      </span>
                                      <p
                                        className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
                                      >
                                        {entry.advancement}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleIndividualApprove(entry.id!)}
                              className={`p-2 rounded-xl transition-all ${
                                isDark
                                  ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                                  : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                              }`}
                              title={t('approval.approve')}
                            >
                              {Icons.check}
                            </button>
                            <button
                              onClick={() =>
                                navigate(
                                  `/manager/approvals/${entry.id}/reject`,
                                )
                              }
                              className={`p-2 rounded-xl transition-all ${
                                isDark
                                  ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                                  : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                              }`}
                              title={t('approval.reject')}
                            >
                              {Icons.x}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RejectEntry: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReject = async () => {
    if (!reason.trim()) {
      showToast(t('approval.rejectReasonRequired'), 'warning');
      return;
    }

    setLoading(true);
    try {
      await timeEntriesAPI.reject(Number(id), reason);
      showToast(t('approval.rejectSuccess'), 'success');
      navigate('/manager/approvals');
    } catch (error) {
      console.error('Failed to reject entry:', error);
      showToast(t('approval.rejectFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm rounded-2xl'
    : 'bg-white border border-slate-200 shadow-sm rounded-2xl';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-rose-500 focus:ring-1 focus:ring-rose-500 transition-colors';

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/manager/approvals')}
            className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          >
            {Icons.chevronLeft}
          </button>
          <h1
            className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('approval.rejectEntry')}
          </h1>
        </div>

        <div className={`${cardClass} p-6`}>
          <div className="mb-6">
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            >
              {t('approval.rejectReason')} *
            </label>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={inputClass}
              placeholder={t('approval.rejectReasonPlaceholder')}
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => navigate('/manager/approvals')}
              className={`px-5 py-2.5 rounded-xl font-medium transition-colors ${
                isDark
                  ? 'bg-slate-900 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleReject}
              disabled={loading}
              className="px-5 py-2.5 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white font-semibold rounded-xl shadow-lg shadow-rose-500/25 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? t('approval.rejecting') : t('approval.rejectEntry')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TimeApproval: React.FC = () => {
  return (
    <Routes>
      <Route index element={<TimeApprovalList />} />
      <Route path=":id/reject" element={<RejectEntry />} />
    </Routes>
  );
};

export default TimeApproval;
